import { Plus, Edit, Eye, EyeOff, Trash2, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAdmin } from './AdminContext';

export const AdminAdsTab = () => {
  const { ads, adStats, adDialogOpen, setAdDialogOpen, editingAd, setEditingAd, newAd, setNewAd, handleCreateAd, handleUpdateAd, toggleAd, deleteAd, openEditAd } = useAdmin();

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2"><Image size={18} /> Ad Management</h2>
        <Button size="sm" className="w-full sm:w-auto" onClick={() => { setEditingAd(null); setNewAd({ title: '', business_name: '', description: '', image_url: '', link_url: '', is_premium: false, is_active: true, placement: 'sidebar', start_date: '', end_date: '', max_impressions: '', max_clicks: '' }); setAdDialogOpen(true); }}>
          <Plus size={14} className="mr-1" /> Create Ad
        </Button>
      </div>

      {adStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
          {[
            { label: 'Total', value: adStats.total_ads },
            { label: 'Active', value: adStats.active_ads, color: 'text-green-500' },
            { label: 'Views', value: adStats.total_impressions },
            { label: 'Clicks', value: adStats.total_clicks },
            { label: 'CTR', value: `${adStats.ctr?.toFixed(2)}%`, span: 'col-span-2 sm:col-span-1' },
          ].map(({ label, value, color, span }) => (
            <Card key={label} className={`bg-card border-border ${span || ''}`}>
              <CardContent className="p-3 sm:pt-4">
                <p className={`text-lg sm:text-2xl font-bold ${color || ''}`}>{value}</p>
                <p className="text-xs sm:text-sm text-muted-foreground">{label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid gap-3">
        {ads.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-6 text-center">
              <Image size={36} className="mx-auto mb-3 text-muted-foreground" />
              <h3 className="text-base font-semibold mb-2">No ads yet</h3>
              <p className="text-sm text-muted-foreground mb-3">Create your first ad.</p>
              <Button size="sm" onClick={() => setAdDialogOpen(true)}><Plus size={14} className="mr-1" /> Create Ad</Button>
            </CardContent>
          </Card>
        ) : (
          ads.map(ad => (
            <Card key={ad.id} className={`bg-card border-border ${!ad.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-3">
                <div className="flex gap-3">
                  {ad.image_url && <img src={ad.image_url} alt={ad.title} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate flex items-center gap-1">{ad.title}{ad.is_premium && <span className="text-yellow-500 text-xs">*</span>}</h3>
                        <p className="text-xs text-muted-foreground truncate">{ad.business_name}</p>
                      </div>
                      {!ad.is_active && <Badge variant="secondary" className="text-[10px] px-1">Off</Badge>}
                    </div>
                    <p className="text-xs mt-1 line-clamp-2">{ad.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-muted-foreground">
                      <span>{ad.impressions || 0} views</span><span>{ad.clicks || 0} clicks</span>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => openEditAd(ad)}><Edit size={12} className="mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => toggleAd(ad.id)}>{ad.is_active ? <EyeOff size={12} /> : <Eye size={12} />}</Button>
                      <Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => deleteAd(ad.id)}><Trash2 size={12} /></Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={adDialogOpen} onOpenChange={setAdDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingAd ? 'Edit Ad' : 'Create New Ad'}</DialogTitle></DialogHeader>
          <form onSubmit={editingAd ? handleUpdateAd : handleCreateAd} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Title *</Label><Input value={newAd.title} onChange={(e) => setNewAd({...newAd, title: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Business Name *</Label><Input value={newAd.business_name} onChange={(e) => setNewAd({...newAd, business_name: e.target.value})} required /></div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={newAd.description} onChange={(e) => setNewAd({...newAd, description: e.target.value})} /></div>
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input value={newAd.image_url} onChange={(e) => setNewAd({...newAd, image_url: e.target.value})} placeholder="https://..." />
              {newAd.image_url && <img src={newAd.image_url} alt="Preview" className="w-32 h-20 object-cover rounded mt-2" />}
            </div>
            <div className="space-y-2"><Label>Link URL</Label><Input value={newAd.link_url} onChange={(e) => setNewAd({...newAd, link_url: e.target.value})} placeholder="https://..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Placement</Label>
                <select className="w-full p-2 rounded border bg-background" value={newAd.placement} onChange={(e) => setNewAd({...newAd, placement: e.target.value})}>
                  <option value="sidebar">Sidebar</option><option value="banner">Banner</option><option value="feed">In Feed</option><option value="interstitial">Interstitial</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="flex gap-4 pt-2">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newAd.is_premium} onChange={(e) => setNewAd({...newAd, is_premium: e.target.checked})} />Premium</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={newAd.is_active} onChange={(e) => setNewAd({...newAd, is_active: e.target.checked})} />Active</label>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={newAd.start_date} onChange={(e) => setNewAd({...newAd, start_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>End Date</Label><Input type="date" value={newAd.end_date} onChange={(e) => setNewAd({...newAd, end_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Max Impressions</Label><Input type="number" value={newAd.max_impressions} onChange={(e) => setNewAd({...newAd, max_impressions: e.target.value})} placeholder="Unlimited" /></div>
              <div className="space-y-2"><Label>Max Clicks</Label><Input type="number" value={newAd.max_clicks} onChange={(e) => setNewAd({...newAd, max_clicks: e.target.value})} placeholder="Unlimited" /></div>
            </div>
            <Button type="submit" className="w-full">{editingAd ? 'Update Ad' : 'Create Ad'}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
